<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'

import { useSourcepointStore } from '../../../stores/sourcepoint'
import { usePageStore } from '../../../stores/page'
import { useDeviceDetection } from '../../../composables/use-device-detection'

const props = withDefaults(defineProps<{
  url: string,
  appendConsentStringToUrl?: boolean,
  placeholder?: string,
  height?: number,
  mobileHeight?: number,
  width?: number,
  allow?: string[],
  scrolling?: string
}>(), {
  appendConsentStringToUrl: false
})

const { deviceType } = useDeviceDetection()
const { consentsExist, consentString } = storeToRefs(useSourcepointStore())
const { pageData } = storeToRefs(usePageStore())

const iframeUrl = computed(() => {
  if (consentsExist.value && consentString.value && props.appendConsentStringToUrl) {
    const url = new URL(props.url)

    // NOTE for now we hardcode the query param with `consent` as only the isNotTV iframe's using it
    //      might have to parametrize this in the future if more iframe providers require it with different names
    url.searchParams.set('consent', consentString.value)

    return url.toString()
  }

  return props.url
})

const iframeWidth = computed(() => props.width ? props.width + 'px' : '100%')

const iframeHeight = computed(() => {
  const height = deviceType.value === 'mobile' ? (props.mobileHeight || props.height) : props.height
  return height + 'px'
})

const iframeAllow = computed(() => props.allow?.length ? props.allow.join('; ') : '')

const iframeScrolling = computed(() => props.scrolling || 'auto')

const loadAdaAutonativeIframeHeightScript = computed(() => {
  return !!(pageData.value?.metaData?.advertorial && consentsExist.value)
})

useHead({
  script: computed(() => loadAdaAutonativeIframeHeightScript.value ? [{
    'data-vmid': 'ada-autonative-iframe-height',
    key: 'ada-autonative-iframe-height',
    src: 'https://static.emsservice.de/responsive/setIframeHeightParent.min.js',
    async: true
  }] : [])
})
</script>

<template>
  <iframe v-if="iframeUrl?.length"
          :scrolling="iframeScrolling"
          :src="iframeUrl"
          :height="iframeHeight"
          :width="iframeWidth"
          :allow="iframeAllow" />
  <div v-else>
    {{ placeholder }}
  </div>
</template>
